import {Card, Container, Group, Menu, rem, Stack, Text, Title, UnstyledButton} from "@mantine/core";
import {useCallback, useMemo, useState} from "react";
import {
  adjustToTimeZone,
  AllocatableLoadSearchCriteria,
  allocatableLoadsSelector,
  useSearchAllocatableLoadsQuery
} from "@ots-frontend/ots-data";
import {IconChevronDown} from "@tabler/icons-react";
import {useTranslation} from "react-i18next";
import {skipToken} from "@reduxjs/toolkit/query";
import {PAGINATION_PAGE_SIZES, usePagination} from "../../components/data-table/DataTable/usePagination";
import {ThemedPage} from "../../components/page/ThemedPage/ThemedPage";
import {ResizeableContent} from "../../components/ResizableContent/ResizableContent";
import {FromToDateToolbar} from "../../components/toolbar/FromToDateToolbar/FromToDateToolbar";
import {AllocatableLoadDataTable} from "../../components/data-table/AllocatableLoadDataTable/AllocatableLoadDataTable";
import {useApp} from "../../providers/RegionProvider/RegionProvider";

export type AllocatableLoadSearchPageProps = {}

export const AllocatableLoadSearchPage = (props: AllocatableLoadSearchPageProps) => {
  const {regionId} = useApp();
  const {t} = useTranslation(["translation", "common"]);

  const [dateCriteria, setDateCriteria] = useState<{ fromDate: Date, toDate: Date | undefined }>()

  const selectAllocatableLoads = useMemo(() => {
    return allocatableLoadsSelector();
  }, []);

  const searchArg: AllocatableLoadSearchCriteria | typeof skipToken = useMemo(() => {
    if (regionId && dateCriteria?.fromDate) {
      return {
        fromDttm: adjustToTimeZone(dateCriteria.fromDate).getTime(),
        toDttm: dateCriteria.toDate ? adjustToTimeZone(dateCriteria.toDate).getTime() : undefined,
        regionId: regionId,
      }
    } else {
      return skipToken
    }
  }, [dateCriteria?.fromDate, dateCriteria?.toDate, regionId])

  const {allocatableLoads, isFetching, refetch} = useSearchAllocatableLoadsQuery(searchArg, {
    selectFromResult: (result) => ({
      ...result,
      allocatableLoads: selectAllocatableLoads(result),
      isFetching: result.isFetching,
    })
  });

  const onFilterChange = useCallback((fromDate: Date, toDate: Date | undefined) => {
    setDateCriteria({
      fromDate: fromDate,
      toDate: toDate
    })
  }, []);

  const pagination = usePagination({
    data: allocatableLoads,
    recordsPerPageOptions: PAGINATION_PAGE_SIZES,
    defaultPageSize: PAGINATION_PAGE_SIZES[1]
  });

  return (
    <ThemedPage>
      {(classes) => (
        <ResizeableContent>
          {(size) => (
            <Container h={size.height} w={size.width} className={classes.container} fluid={true} p="md">
              <Stack h="100%" m="sm">
                <Card shadow="sm" padding="lg" mb="lg" radius="md" withBorder>
                  <Group position="apart">
                    <Title
                      order={4}>{t("page.allocatableloads.title", "Allocatable Loads", {ns: "translation"})}</Title>
                    <Menu
                      width={260}
                      position="bottom-end"
                      transitionProps={{transition: "pop-top-right"}}
                      withinPortal
                    >
                      <Menu.Target>
                        <UnstyledButton>
                          <Group spacing={7}>
                            <Text weight={500} size="sm" sx={{lineHeight: 1}} mr={3}>
                              {t('common.action.plural', "Actions")}
                            </Text>
                            <IconChevronDown size={rem(12)} stroke={1.5}/>
                          </Group>
                        </UnstyledButton>
                      </Menu.Target>
                      <Menu.Dropdown>
                        <Menu.Item onClick={() => refetch()}>
                          {t("actions.refresh", "Refresh", {ns: "common"})}
                        </Menu.Item>
                      </Menu.Dropdown>
                    </Menu>
                  </Group>
                  <FromToDateToolbar
                    defaultValues={{
                      fromDate: dateCriteria?.fromDate,
                      toDate: dateCriteria?.toDate
                    }}
                    loading={isFetching}
                    onSubmit={onFilterChange}/>
                </Card>
                <AllocatableLoadDataTable
                  {...pagination}
                  withBorder
                  fetching={isFetching}/>
              </Stack>
            </Container>
          )}
        </ResizeableContent>
      )}
    </ThemedPage>
  );
};
